"use client"

import { useEffect, useState, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  MenuItem,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Divider,
} from "@mui/material"
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Phone as PhoneIcon,
  Link as LinkIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  UploadFile as UploadFileIcon,
  Image as ImageIcon,
  Videocam as VideocamIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material"
import DashboardLayout from "../components/DashboardLayout"
import { getUserCampaigns, getCampaignById, updateCampaign, resetCampaignState } from "../slices/campaignSlice"
import { API_URL } from "../constants"
import CampaignReportDialog from "../components/campaigns/CampaignReportDialog"
import CampaignDetailsDialog from "../components/campaigns/CampaignDetailsDialog" // Import the centralized dialog

const MyCampaignsPage = () => {
  const dispatch = useDispatch()
  const { userInfo } = useSelector((state) => state.auth || {})
  const {
    campaigns = [],
    loading = false,
    error = null,
    success = false,
    campaign: campaignDetails = null, // Campaign details for edit or view
    pagination = { page: 1, pages: 1 },
  } = useSelector((state) => state.campaigns || {})

  const [searchQuery, setSearchQuery] = useState("")
  const [viewDialogOpen, setViewDialogOpen] = useState(false) // State for the centralized view dialog
  const [selectedCampaignForView, setSelectedCampaignForView] = useState(null) // Campaign data for the view dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editFormData, setEditFormData] = useState({
    title: "",
    message: "",
    buttons: [],
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [media, setMedia] = useState([]) // State for media files during edit
  const [maxMedia, setMaxMedia] = useState(1)
  const mediaInputRef = useRef(null)
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const [selectedCampaignForReport, setSelectedCampaignForReport] = useState(null)

  const [page, setPage] = useState(1)

  useEffect(() => {
    if (!userInfo || !userInfo.token) {
      toast.error("Authentication required")
      return
    }

    fetchCampaigns()
  }, [dispatch, page, userInfo])
  
  const fetchCampaigns = () => {
    if (!userInfo || !userInfo.token) return

    dispatch(getUserCampaigns({ page }))
      .unwrap()
      .catch((err) => {
        toast.error("Failed to load campaigns")
        console.error("Error loading campaigns:", err)
      })
  }

  useEffect(() => {
    const fetchCampaignConfig = async () => {
      if (!userInfo || !userInfo.token) return

      try {
        const response = await fetch(`${API_URL}/api/campaign-config`, {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        })
        const data = await response.json()

        const numberOfImagesConfig = data.find((config) => config.name === "Number of Images")
        setMaxMedia(numberOfImagesConfig ? Number.parseInt(numberOfImagesConfig.value, 10) : 1)
      } catch (error) {
        console.error("Failed to fetch campaign config:", error)
      }
    }

    fetchCampaignConfig()
  }, [userInfo])

  useEffect(() => {
    if (success && editDialogOpen) {
      setEditDialogOpen(false)
      dispatch(resetCampaignState())
      fetchCampaigns() // Refresh campaigns after successful edit
    }
  }, [success, dispatch, editDialogOpen, page])

  useEffect(() => {
    if (error) {
      toast.error(error)
      dispatch(resetCampaignState())
    }
  }, [error, dispatch])

  useEffect(() => {
    // This effect is for populating edit form data when campaignDetails for edit is fetched
    if (campaignDetails && editDialogOpen) {
      // Only populate if edit dialog is open
      setEditFormData({
        title: campaignDetails.title || "",
        message: campaignDetails.message || "",
        buttons: campaignDetails.buttons || [],
      })
      setMedia(campaignDetails.media || [])
    }
  }, [campaignDetails, editDialogOpen])

  const handleSearch = () => {
    // Implement search functionality (currently just logs)
    // To implement actual search, you'd modify getUserCampaigns to accept a search query
    // dispatch(getUserCampaigns({ page, searchQuery }));
  }

  const handleViewCampaign = (campaignId) => {
    // Fetch full campaign details for the view dialog
    dispatch(getCampaignById(campaignId))
      .unwrap()
      .then((result) => {
        setSelectedCampaignForView(result.campaign) // Set campaign for the centralized view dialog
        setViewDialogOpen(true) // Open the centralized view dialog
      })
      .catch((err) => {
        toast.error("Failed to fetch campaign details")
        console.error("Error fetching campaign for view:", err)
      })
  }

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false)
    setSelectedCampaignForView(null) // Clear selected campaign when dialog closes
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  }

  // This getMediaUrl is for the EDIT dialog's media display and upload
  const getMediaUrl = (media) => {
    if (!media || !media.url) return

    // If media.url is already a full URL, return it
    if (media.url.startsWith("http://") || media.url.startsWith("https://")) {
      return media.url
    }

    // Otherwise, prepend API_URL to the relative path
    return `${API_URL}${media.url.startsWith("/") ? media.url : `/${media.url}`}`
  }

  const handleOpenEditDialog = (campaign) => {
    dispatch(getCampaignById(campaign._id))
      .unwrap()
      .then((result) => {
        const campaignData = result.campaign
        setEditFormData({
          title: campaignData.title || "",
          message: campaignData.message || "",
          buttons: campaignData.buttons || [],
        })
        setMedia(campaignData.media || [])
        setEditDialogOpen(true)
      })
      .catch((error) => {
        toast.error("Failed to fetch campaign details for editing")
        console.error("Error fetching campaign:", error)
      })
  }

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false)
    setEditFormData({
      title: "",
      message: "",
      buttons: [],
    })
    setMedia([])
  }

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false)
    setSelectedCampaignForReport(null)
  }

  const handleEditInputChange = (e) => {
    const { name, value } = e.target
    setEditFormData({
      ...editFormData,
      [name]: value,
    })
  }

  const handleButtonChange = (index, field, value) => {
    const updatedButtons = [...editFormData.buttons]
    updatedButtons[index] = {
      ...updatedButtons[index],
      [field]: value,
    }
    setEditFormData({
      ...editFormData,
      buttons: updatedButtons,
    })
  }

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files)
    if (!files || files.length === 0) return

    if (media.length + files.length > maxMedia) {
      toast.error(`You can only upload ${maxMedia} media files`)
      return
    }

    setIsUploading(true)
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData()
        formData.append("file", file)

        const response = await fetch(`${API_URL}/api/upload`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
          body: formData,
        })

        if (!response.ok) {
          throw new Error(`Failed to upload file: ${response.statusText}`)
        }

        const data = await response.json()

        if (data.success) {
          let type = "image"
          if (file.type.includes("video")) {
            type = "video"
          } else if (file.type.includes("pdf")) {
            type = "pdf"
          }

          // data.filePath from backend is already `/uploads/filename.ext`
          const fileObj = {
            name: file.name,
            url: data.filePath, // Use data.filePath directly
            type,
          }
          return fileObj
        } else {
          throw new Error(data.message || "Failed to upload file")
        }
      })

      const uploadedMedia = await Promise.all(uploadPromises)
      setMedia([...media, ...uploadedMedia])
      toast.success(`Media uploaded successfully`)
    } catch (error) {
      toast.error(`Failed to upload media: ${error.message}`)
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveMedia = (index) => {
    setMedia((prevMedia) => prevMedia.filter((_, i) => i !== index))
  }

  const handleOpenReportDialog = (campaign) => {
    setSelectedCampaignForReport(campaign)
    setReportDialogOpen(true)
  }

  const handleSubmitEdit = () => {
    if (!campaignDetails) return // Use campaignDetails from state

    setIsSubmitting(true)

    // Prepare data for submission
    const campaignData = {
      title: editFormData.title,
      message: editFormData.message,
      media: media.map((m) => ({ type: m.type, url: m.url, name: m.name })),
    }

    // Only include buttons if it's a button campaign
    if (campaignDetails.campaignType === "button" && editFormData.buttons.length > 0) {
      campaignData.buttons = editFormData.buttons
    }

    dispatch(updateCampaign({ id: campaignDetails._id, campaignData }))
      .unwrap()
      .then(() => {
        toast.success("Campaign updated successfully")
        setEditDialogOpen(false)
        fetchCampaigns()
        setIsSubmitting(false)
      })
      .catch((error) => {
        toast.error(error || "Failed to update campaign")
        setIsSubmitting(false)
      })
  }

  const handlePageChange = (event, value) => {
    setPage(value)
  }

  const filteredCampaigns =
    campaigns && campaigns.length > 0
      ? campaigns.filter(
          (campaign) =>
            (campaign.title?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
            (campaign.message?.toLowerCase() || "").includes(searchQuery.toLowerCase()),
        )
      : []

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1200, mx: "auto", p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1">
            My Campaigns
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              placeholder="Search campaigns..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              sx={{
                bgcolor: "#008069",
                "&:hover": { bgcolor: "#128C7E" },
              }}
            >
              Search
            </Button>
          </Box>
        </Box>

        <Paper sx={{ mb: 4 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Numbers</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredCampaigns.length > 0 ? (
                  filteredCampaigns.map((campaign) => (
                    <TableRow key={campaign._id} hover
                    sx={{backgroundColor :campaign.status ==="finished" && campaign.isWhitelistCampaign && campaign?.rejectionReason &&  campaign?.rejectionReason !== "" && userInfo.user.role==="admin" ? "#e99b9bff" : ""}}

                    >
                      <TableCell>{campaign.title || campaign.name || "Untitled"}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                          <span>{campaign.campaignType === "button" ? "Button" : "Normal"} {campaign.campaignOption}</span>
                          {campaign.executionMode === "python_demo" && (
                            <Chip label="DEMO · PYTHON" color="secondary" size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={campaign.status.toUpperCase()}
                          color={
                            campaign.status === "pending"
                              ? "warning"
                              : campaign.status === "processing"
                                ? "info"
                                : campaign.status === "finished"
                                  ? "success"
                                  : "error"
                          }
                          size="small"
                        />
                        {campaign.executionMode === "python_demo" && campaign.pythonExecution?.status && (
                          <Chip
                            label={`EXECUTION: ${campaign.pythonExecution.status.toUpperCase()}`}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 0.75 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>{campaign.userId?.name || "Unknown"}</TableCell>
                      <TableCell>{formatDate(campaign.createdAt)}</TableCell>
                      <TableCell>{campaign.totalNumbers || 0}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewCampaign(campaign._id)} // Opens the centralized CampaignDetailsDialog
                          >
                            View
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenEditDialog(campaign)}
                            sx={{ ml: 1 }}
                            color="primary"
                          >
                            Edit
                          </Button>
                          {campaign.status === "finished" && (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<DownloadIcon />}
                              onClick={() => handleOpenReportDialog(campaign)}
                              sx={{
                                ml: 1,
                                bgcolor: "#007bff", // A blue color for reports
                                "&:hover": { bgcolor: "#0056b3" },
                              }}
                            >
                              Report
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No campaigns found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination.pages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3, pb: 2 }}>
              <Pagination count={pagination.pages} page={page} onChange={handlePageChange} color="primary" />
            </Box>
          )}
        </Paper>

        {/* Centralized Campaign Details Dialog */}
        <CampaignDetailsDialog
          open={viewDialogOpen}
          onClose={handleCloseViewDialog}
          campaign={selectedCampaignForView}
          userInfo={userInfo} // Pass userInfo for API calls within the dialog
          fetchCampaigns={fetchCampaigns} // Pass fetchCampaigns to allow refreshing list after actions
          showProcessingTimeline={false} // Crucial: Hide processing section for My Campaigns page
          // These props are for admin campaign progression, not used here but passed for compatibility
          onOpenFindNonWhatsAppDialog={() => {}}
          onSkipFiltering={() => {}}
          onComplete={() => {}}
          onReject={() => {}}
          onResetCampaign={() => {}}
          isSubmitting={false}
          isAutomatic={false}
          checkValidationStatus={() => {}} // Dummy function
        />

        {/* Edit Campaign Dialog (retains its own media display) */}
        <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            Edit Campaign
            <IconButton
              aria-label="close"
              onClick={handleCloseEditDialog}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
              disabled={isSubmitting}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {campaignDetails && ( // Use campaignDetails from state
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Campaign Title"
                      name="title"
                      value={editFormData.title}
                      onChange={handleEditInputChange}
                      fullWidth
                      margin="normal"
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Message"
                      name="message"
                      value={editFormData.message}
                      onChange={handleEditInputChange}
                      fullWidth
                      multiline
                      rows={6}
                      margin="normal"
                      disabled={isSubmitting}
                    />
                  </Grid>
                  {/* Media Upload */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Media Files
                    </Typography>
                    <input
                      type="file"
                      accept="image/*,video/*,.pdf"
                      onChange={handleMediaUpload}
                      ref={mediaInputRef}
                      style={{ display: "none" }}
                      disabled={isSubmitting}
                      multiple
                    />
                    <Button
                      variant="outlined"
                      onClick={() => mediaInputRef.current.click()}
                      disabled={isSubmitting}
                      fullWidth
                      startIcon={<UploadFileIcon />}
                    >
                      Upload Media
                    </Button>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">Uploaded Media:</Typography>
                      <Grid container spacing={2}>
                        {media.map((item, index) => {
                          const fileName = decodeURIComponent(item.name.split("/").pop()) // Extract clean filename
                          let fileIcon = null
                          if (item.type?.startsWith("image")) {
                            fileIcon = <ImageIcon fontSize="small" />
                          } else if (item.type?.startsWith("video")) {
                            fileIcon = <VideocamIcon fontSize="small" />
                          } else if (item.type === "application/pdf") {
                            fileIcon = <PictureAsPdfIcon fontSize="small" />
                          } else {
                            fileIcon = <DescriptionIcon fontSize="small" /> // Generic file icon
                          }

                          return (
                            <Grid item xs={12} sm={6} md={4} key={item.url}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  mt: 1,
                                  p: 1,
                                  border: "1px solid #e0e0e0",
                                  borderRadius: 1,
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
                                  {fileIcon}
                                  <Typography
                                    variant="body2"
                                    sx={{ ml: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                  >
                                    {fileName}
                                  </Typography>
                                </Box>
                                <IconButton onClick={() => handleRemoveMedia(index)} disabled={isSubmitting}>
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Grid>
                          )
                        })}
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Media Previews */}
                  {media.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        Media Previews:
                      </Typography>
                      <Grid container spacing={2}>
                        {media.map((item, index) => {
                          const mediaUrl = getMediaUrl(item)
                          const fileName = decodeURIComponent(item.name.split("/").pop()) // Extract clean filename

                          return (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 150, // Fixed height for consistent preview box size
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "1px solid #e0e0e0",
                                  borderRadius: "8px",
                                  overflow: "hidden",
                                  position: "relative",
                                  bgcolor: "#f0f0f0", // Light background for empty/pdf previews
                                }}
                              >
                                {item.type?.startsWith("image") && (
                                  <img
                                    src={mediaUrl || `${process.env.REACT_APP_API_URL}/placeholder.svg`}
                                    alt={`Media Preview ${fileName}`}
                                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                    onError={(e) => {
                                      e.target.onerror = null
                                      e.target.src = `${process.env.REACT_APP_API_URL}/placeholder.svg`
                                    }}
                                  />
                                )}
                                {item.type?.startsWith("video") && (
                                  <video
                                    controls
                                    src={mediaUrl}
                                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                  >
                                    Your browser does not support the video tag.
                                  </video>
                                )}
                                {item.type === "application/pdf" && (
                                  <Box sx={{ textAlign: "center", color: "text.secondary" }}>
                                    <PictureAsPdfIcon sx={{ fontSize: 48, mb: 1 }} />
                                    <Typography variant="body2">PDF: {fileName}</Typography>
                                    <Button
                                      variant="text"
                                      size="small"
                                      href={mediaUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ mt: 1 }}
                                    >
                                      View PDF
                                    </Button>
                                  </Box>
                                )}
                                {!item.type?.startsWith("image") &&
                                  !item.type?.startsWith("video") &&
                                  item.type !== "application/pdf" && (
                                    <Box sx={{ textAlign: "center", color: "text.secondary" }}>
                                      <DescriptionIcon sx={{ fontSize: 48, mb: 1 }} />
                                      <Typography variant="body2">File: {fileName}</Typography>
                                    </Box>
                                  )}
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  mt: 0.5,
                                  display: "block",
                                  textAlign: "center",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {fileName}
                              </Typography>
                            </Grid>
                          )
                        })}
                      </Grid>
                    </Grid>
                  )}

                  {/* Button Campaign Specific Fields */}
                  {campaignDetails.campaignType === "button" &&
                    editFormData.buttons &&
                    editFormData.buttons.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                          Buttons
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {editFormData.buttons.map((button, index) => (
                          <Box key={index} sx={{ mb: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Button {index + 1}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  label="Button Text"
                                  value={button.text || ""}
                                  onChange={(e) => handleButtonChange(index, "text", e.target.value)}
                                  fullWidth
                                  margin="normal"
                                  disabled={isSubmitting}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <FormControl fullWidth margin="normal" disabled={isSubmitting}>
                                  <InputLabel>Button Type</InputLabel>
                                  <Select
                                    value={button.type || "link"}
                                    onChange={(e) => handleButtonChange(index, "type", e.target.value)}
                                    label="Button Type"
                                  >
                                    <MenuItem value="link">Link</MenuItem>
                                    <MenuItem value="call">Call</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  label={button.type === "call" ? "Phone Number" : "URL"}
                                  value={button.value || ""}
                                  onChange={(e) => handleButtonChange(index, "value", e.target.value)}
                                  fullWidth
                                  margin="normal"
                                  disabled={isSubmitting}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        {button.type === "call" ? <PhoneIcon /> : <LinkIcon />}
                                      </InputAdornment>
                                    ),
                                  }}
                                  helperText={
                                    button.type === "call"
                                      ? "Enter phone number with country code"
                                      : "Enter URL starting with http:// or https://"
                                  }
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                      </Grid>
                    )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button
              color="primary"
              variant="contained"
              onClick={handleSubmitEdit}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={isSubmitting}
              sx={{
                bgcolor: "#008069",
                "&:hover": { bgcolor: "#128C7E" },
              }}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Campaign Report Dialog */}
        <CampaignReportDialog
          open={reportDialogOpen}
          onClose={handleCloseReportDialog}
          campaign={selectedCampaignForReport}
          userInfo={userInfo}
        />
      </Box>
    </DashboardLayout>
  )
}

export default MyCampaignsPage
